// Case-load analytics for the board: how many ORs are still running late in the day, and how many
// rooms are running at each point in it. Kept as pure functions over "case windows" so the per-site
// after-3pm bubble, the load strip under the float row, the combined header roll-up, and the tests
// all read from ONE definition of each count instead of several near-copies in the view.

/** A case reduced to local minutes-of-day, tagged with the room and site it belongs to. */
export interface CaseWindow {
  roomId: string;
  hospitalId: string;
  startMin: number;
  endMin: number;
}

export const DAY_MINUTES = 24 * 60;
/** The day's pivot: 15:00 local. The board already draws its FINISH line here. */
export const AFTER_HOURS_CUTOFF_MIN = 15 * 60;
/** The load strip's cell width in minutes — half-hour granularity. */
export const BUCKET_MINUTES = 30;

/** Local minutes since midnight for a UTC instant — the board positions every case this way. */
export const localMinutesOfDay = (utcIso: string): number => {
  const d = new Date(utcIso);
  const t = d.getTime();
  if (!Number.isFinite(t)) return NaN;
  return d.getHours() * 60 + d.getMinutes();
};

/** The LOCAL calendar date of a UTC instant, as "YYYY-MM-DD" (lexicographic order = date order). */
export const localDateOf = (utcIso: string): string => {
  const d = new Date(utcIso);
  if (!Number.isFinite(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * Build a day-clamped window for one case.
 *
 * A case that runs past local midnight reads back as an END minutes-of-day EARLIER than its start
 * (e.g. 22:00 -> 01:00 becomes 1320 -> 60). Clamping those to midnight keeps them occupying the rest
 * of the strip instead of inverting into a negative-width window that would silently vanish from
 * every count below.
 *
 * Pass `day` (the board's local "YYYY-MM-DD") whenever the cases come from a by-date search. That
 * search is OVERLAP-based, so it also returns last night's case running 22:00 -> 02:00 into today.
 * Minutes-of-day alone cannot tell that case from one starting at 22:00 TONIGHT, so without `day` it
 * lands at 22:00-24:00 today: counted as a room running after 3pm, as running live tonight, and in
 * tonight's load-strip cells. With `day`, each end is anchored to its own calendar date -- a start on
 * an earlier day clamps to 00:00, an end on a later day clamps to 24:00, and a case wholly outside
 * the day is dropped.
 */
export const caseWindow = (
  startUtc: string,
  endUtc: string,
  roomId: string,
  hospitalId: string,
  day?: string,
): CaseWindow | null => {
  if (!startUtc || !endUtc) return null;
  const startMin = localMinutesOfDay(startUtc);
  const rawEndMin = localMinutesOfDay(endUtc);
  if (!Number.isFinite(startMin) || !Number.isFinite(rawEndMin)) return null;
  if (day) {
    const startDay = localDateOf(startUtc);
    const endDay = localDateOf(endUtc);
    if (startDay > day || endDay < day) return null;            // not on this day at all
    const s = startDay < day ? 0 : startMin;                    // carried in from last night
    const e = endDay > day ? DAY_MINUTES : rawEndMin;           // runs on past tonight's midnight
    return e > s ? { roomId, hospitalId, startMin: s, endMin: e } : null;
  }
  // <= start covers both the midnight crossing and a zero-length case.
  const endMin = rawEndMin <= startMin ? DAY_MINUTES : rawEndMin;
  return { roomId, hospitalId, startMin, endMin };
};

/**
 * The earliest startMin across some case windows, or null when there are none. Takes every window at
 * face value: a carried-in case that caseWindow(day) clamped to 00:00 WILL win here. Callers that
 * want the day's first real start use firstCaseStartOnDay, which drops those before they arrive.
 */
export const earliestCaseStartMin = (windows: CaseWindow[]): number | null => {
  let min: number | null = null;
  for (const w of windows) if (min === null || w.startMin < min) min = w.startMin;
  return min;
};

/**
 * The local minute of `day`'s FIRST case start, or null when no case starts on `day`.
 *
 * Used to park the board's time bar somewhere something is actually happening. The old default of
 * 07:00 sat just before most sites open, so a simulation opened on a board reading "0 running" at
 * several sites (County Hospital 3's earliest case on 2026-10-10 starts 07:15).
 *
 * Only cases that START on `day` count. The by-date search is overlap-based, so it also returns last
 * night's case running into this morning, and caseWindow(day) CLAMPS that one to 00:00 rather than
 * dropping it (right for the running counts, wrong here) -- its 0 would win every comparison and park
 * the bar at midnight. The same filter makes the previous day's cases, still in state for a moment
 * after a date change, count for nothing.
 */
export const firstCaseStartOnDay = (
  cases: { startTimeUtc: string; endTimeUtc: string }[],
  day: string,
): number | null =>
  earliestCaseStartMin(cases
    .filter(c => localDateOf(c.startTimeUtc) === day)
    .map(c => caseWindow(c.startTimeUtc, c.endTimeUtc, '', '', day))
    .filter((w): w is CaseWindow => w !== null));

/**
 * "ORs running after 3pm", per Braden's definition: a room is in the count when any of its cases is
 * in progress and runs TO or PAST the cutoff, OR starts at the cutoff. Both halves reduce to the one
 * test `endMin >= cutoff` — a case still going at 1500 ends at/after 1500, and a case that starts at
 * 1500 ends later still. Counts distinct ROOMS, so three cases in one OR count once.
 */
export const roomsRunningAfter = (windows: CaseWindow[], cutoffMin: number): number => {
  const rooms = new Set<string>();
  for (const w of windows) if (w.endMin >= cutoffMin) rooms.add(w.roomId);
  return rooms.size;
};

/** How many CASES cover a given minute. Half-open [start, end), so a case ending at 1500 is not
 *  "running at" 1500 — roomsRunningAfter asks a different question and counts it. */
export const concurrencyAt = (windows: CaseWindow[], min: number): number =>
  windows.reduce((n, w) => (w.startMin <= min && min < w.endMin ? n + 1 : n), 0);

/**
 * How many distinct ROOMS are running at a given minute. This is what the strip reports: an OR board
 * counts rooms, and two overlapping cases in one room still occupy one room — which is also why this
 * cannot be replaced by concurrencyAt.
 */
export const roomsRunningAt = (windows: CaseWindow[], min: number): number => {
  const rooms = new Set<string>();
  for (const w of windows) if (w.startMin <= min && min < w.endMin) rooms.add(w.roomId);
  return rooms.size;
};

/**
 * Peak concurrent ROOMS inside each bucket of the day, sampled every `stepMin` minutes so a short
 * case that sits wholly inside one bucket still registers. Returns DAY_MINUTES / bucketMin entries —
 * 48 at the default half-hour width, matching the strip's cells.
 */
export const peakRoomsPerBucket = (
  windows: CaseWindow[],
  bucketMin: number = BUCKET_MINUTES,
  stepMin = 5,
): number[] => {
  const bucket = Math.max(1, bucketMin);
  const step = Math.max(1, Math.min(stepMin, bucket));
  const out: number[] = [];
  for (let start = 0; start < DAY_MINUTES; start += bucket) {
    let peak = 0;
    for (let m = start; m < start + bucket; m += step) {
      const n = roomsRunningAt(windows, m);
      if (n > peak) peak = n;
    }
    out.push(peak);
  }
  return out;
};

/** The day's busiest bucket — the site's peak concurrent rooms, shown beside the strip. */
export const peakConcurrency = (buckets: number[]): number =>
  buckets.reduce((a, b) => Math.max(a, b), 0);

export type LoadBand = 'low' | 'medium' | 'high' | 'critical';

/**
 * Absolute load bands, by Braden's thresholds: 0-9 / 10-19 / 20-29 / 30+.
 *
 * Deliberately ABSOLUTE rather than relative to the day's peak. Scaling against the peak made a
 * quiet site's busiest hour glow the same red as a genuinely overloaded one, which is what made the
 * strip read as red everywhere. The top band is open-ended so 40+ stays in the most severe colour
 * instead of wrapping back to a calmer one.
 */
export const loadBand = (count: number): LoadBand => {
  if (count >= 30) return 'critical';
  if (count >= 20) return 'high';
  if (count >= 10) return 'medium';
  return 'low';
};
